/**
 * SSH Key Pair Generation using Web Crypto API.
 * Generates RSA-4096 key pairs and converts to OpenSSH format.
 */

/**
 * Generate an RSA-4096 SSH key pair.
 * @param {string} comment - Key comment (e.g. username@reporthub)
 * @returns {Promise<{publicKey: string, privateKey: string}>}
 */
export async function generateSshKeyPair(comment = '') {
  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'RSASSA-PKCS1-v1_5', modulusLength: 4096, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
    true,
    ['sign', 'verify']
  );

  const [spkiBuffer, pkcs8Buffer] = await Promise.all([
    window.crypto.subtle.exportKey('spki', keyPair.publicKey),
    window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey),
  ]);

  const publicKey = spkiToOpenSsh(new Uint8Array(spkiBuffer), comment);
  const privateKey = pkcs8ToPem(new Uint8Array(pkcs8Buffer));

  return { publicKey, privateKey };
}

/** Convert SPKI public key to OpenSSH ssh-rsa format */
function spkiToOpenSsh(spkiBytes, comment) {
  // Parse the SPKI ASN.1 to extract modulus and exponent
  const { modulus, exponent } = parseSpki(spkiBytes);

  // Build SSH wire format: string "ssh-rsa", mpint exponent, mpint modulus
  const keyType = new TextEncoder().encode('ssh-rsa');
  const parts = [encodeString(keyType), encodeMpint(exponent), encodeMpint(modulus)];
  const totalLen = parts.reduce((sum, p) => sum + p.length, 0);
  const wireKey = new Uint8Array(totalLen);
  let offset = 0;
  for (const part of parts) {
    wireKey.set(part, offset);
    offset += part.length;
  }

  const b64 = btoa(String.fromCharCode(...wireKey));
  return `ssh-rsa ${b64}${comment ? ` ${comment}` : ''}`;
}

/** Parse SPKI DER to extract RSA modulus and exponent */
function parseSpki(der) {
  // SPKI wraps RSAPublicKey in a SEQUENCE { AlgorithmIdentifier, BIT STRING { RSAPublicKey } }
  // We need to find the BIT STRING containing the RSAPublicKey
  let pos = 0;

  function readTag() {
    const tag = der[pos++];
    let len = der[pos++];
    if (len & 0x80) {
      const numBytes = len & 0x7f;
      len = 0;
      for (let i = 0; i < numBytes; i++) {
        len = (len << 8) | der[pos++];
      }
    }
    return { tag, len, start: pos };
  }

  // Outer SEQUENCE
  readTag();
  // AlgorithmIdentifier SEQUENCE — skip it
  const algId = readTag();
  pos = algId.start + algId.len;
  // BIT STRING
  readTag();
  pos++; // skip unused bits byte

  // Now we're at the RSAPublicKey SEQUENCE
  readTag(); // inner SEQUENCE
  // modulus INTEGER
  const modTag = readTag();
  const modulus = der.slice(modTag.start, modTag.start + modTag.len);
  pos = modTag.start + modTag.len;
  // exponent INTEGER
  const expTag = readTag();
  const exponent = der.slice(expTag.start, expTag.start + expTag.len);

  return {
    modulus: stripLeadingZeros(modulus),
    exponent: stripLeadingZeros(exponent),
  };
}

function stripLeadingZeros(bytes) {
  let i = 0;
  while (i < bytes.length - 1 && bytes[i] === 0) i++;
  return bytes.slice(i);
}

/** Encode a byte array as SSH string (4-byte length prefix + data) */
function encodeString(data) {
  const buf = new Uint8Array(4 + data.length);
  new DataView(buf.buffer).setUint32(0, data.length);
  buf.set(data, 4);
  return buf;
}

/** Encode a byte array as SSH mpint (4-byte length prefix + data, with leading 0x00 if high bit set) */
function encodeMpint(data) {
  const needsPad = data[0] & 0x80;
  const len = data.length + (needsPad ? 1 : 0);
  const buf = new Uint8Array(4 + len);
  new DataView(buf.buffer).setUint32(0, len);
  if (needsPad) {
    buf[4] = 0;
    buf.set(data, 5);
  } else {
    buf.set(data, 4);
  }
  return buf;
}

/** Convert PKCS#8 private key to PEM format */
function pkcs8ToPem(pkcs8Bytes) {
  const b64 = btoa(String.fromCharCode(...pkcs8Bytes));
  const lines = b64.match(/.{1,64}/g) || [];
  return `-----BEGIN PRIVATE KEY-----\n${lines.join('\n')}\n-----END PRIVATE KEY-----\n`;
}

/** Trigger a file download in the browser */
export function downloadPrivateKey(privateKeyPem, username) {
  const blob = new Blob([privateKeyPem], { type: 'application/x-pem-file' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${username || 'pickup-user'}_private_key.pem`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
